import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useLocation } from 'react-router-dom';
import firebase from 'firebase/app';
import { usePostHog } from 'posthog-js/react';

import useNavigateWithSearch from '../../../../hooks/useNavigateWithSearch';

import { filterEnabledItems } from '../../utils/utils';
import { getConfigurations, getMembershipProductsPrices, getOrderCheckoutSession, getSubscriptionsPlans } from '../../utils/service';
import { STRIPE_CHECKOUT_SESSION_STATUS } from '../../config/stripe';
import { getSearchParam } from '../../utils/routes';
import {
  SUBSCRIPTION_FROM_CHECKOUT_PARAM,
  STRIPE_CHECKOUT_SESSION_ID_PARAM,
  STRIPE_PAYMENT_INTENT_ID_PARAM,
  isSubscriptionDevSuccessPreview,
  REGISTER_CODE_PARAM,
  REGISTER_FROM_CHECKOUT_PARAM,
  ROUTE_REGISTER,
  ROUTE_UNLIMITED,
  ROUTE_TRASHIE_UNLIMITED,
} from '../../config/routes';
import {
  SUBSCRIPTION_STEPS,
  getOrderSubtotal,
  setSubscriptionOrderItemsInStorage,
  getSubscriptionOrderItemsFromStorage,
  SUBSCRIPTION_PAYMENT_TYPES,
  getSubscriptionPlanValues,
  getSubscriptionPlanInterval,
} from '../../utils/subscriptions';
import { getFilteredMembershipItems, MEMBERSHIP_DEFAULT_ORDER_ITEMS } from '../../utils/membership';
import { RECYCLING_PRODUCT_TYPES } from '../../utils/tbbRegistration';
import { isMobile, SIZE_XS, SIZE_SM } from '../../../../hooks/useBreakPoint';

const SubscriptionContext = createContext();

export const useSubscription = () => useContext(SubscriptionContext);

export const SubscriptionProvider = ({ children }) => {
  const navigateWithSearch = useNavigateWithSearch();
  const contentScrollRef = useRef(null);
  const { pathname, search } = useLocation();
  const isMobileSize = isMobile([SIZE_XS, SIZE_SM]);
  const posthog = usePostHog();
  const isSignInWithEmailLink = firebase.auth().isSignInWithEmailLink(window.location.href);
  const query = new URLSearchParams(search);
  const isFromCheckout = getSearchParam(query, SUBSCRIPTION_FROM_CHECKOUT_PARAM) === 'true';
  const checkoutSessionId = getSearchParam(query, STRIPE_CHECKOUT_SESSION_ID_PARAM);
  const paymentIntentId = getSearchParam(query, STRIPE_PAYMENT_INTENT_ID_PARAM) || getSearchParam(query, 'payment_intent');
  const registerCode = getSearchParam(query, REGISTER_CODE_PARAM);

  const isDirectPayment = pathname === ROUTE_TRASHIE_UNLIMITED;
  const isAnonymous = pathname === ROUTE_UNLIMITED || isDirectPayment;

  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(() => {
    if (
      isFromCheckout
      && (!!checkoutSessionId || !!paymentIntentId || isSubscriptionDevSuccessPreview())
    ) {
      return SUBSCRIPTION_STEPS.CONFIRMATION;
    }

    // /trashie-unlimited uses payment → confirmation (selection step skipped in UI)
    return SUBSCRIPTION_STEPS.PAYMENT;
  });
  const [orderItems, setOrderItems] = useState(() => {
    const orderItemsFromStorage = getSubscriptionOrderItemsFromStorage();
    const defaultOrderItems = orderItemsFromStorage || MEMBERSHIP_DEFAULT_ORDER_ITEMS;

    if (!orderItemsFromStorage) {
      setSubscriptionOrderItemsInStorage(defaultOrderItems);
    }

    return defaultOrderItems;
  });
  const [productPrices, setProductPrices] = useState({});
  const [subscriptionPlan, setSubscriptionPlan] = useState({});
  const [optionSelected, setOptionSelected] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectionScrollReentryCount, setSelectionScrollReentryCount] = useState(0);
  const [elementsPaymentResult, setElementsPaymentResult] = useState(null);
  const previousSubscriptionStepRef = useRef(null);

  const loadData = useCallback(async () => {
    setIsLoading(true);

    const data = await Promise.all([
      getConfigurations('membershipSubscriptionConfig'),
      getMembershipProductsPrices(),
      getSubscriptionsPlans(),
    ]);

    const {
      value: configValue,
      error: configError,
    } = data[0];

    const {
      data: productsPricesData,
      error: productsPricesError,
    } = data[1];

    const {
      data: subscriptionPlanData,
      error: subscriptionPlanError,
    } = data[2];

    setConfig(configError ? {} : configValue);
    setProductPrices(productsPricesError ? {} : productsPricesData);

    if (subscriptionPlanError) {
      setSubscriptionPlan({});
    } else {
      const { value, prevValue } = getSubscriptionPlanValues(subscriptionPlanData);

      setSubscriptionPlan({
        ...subscriptionPlanData,
        value,
        prevValue,
        interval: getSubscriptionPlanInterval(subscriptionPlanData?.interval),
      });
    }

    setIsLoading(false);
  }, []);

  const onOrderItemsChange = useCallback((itemId, count) => {
    setOrderItems(prev => {
      const newOrderItems =
        [RECYCLING_PRODUCT_TYPES.VIRTUAL, RECYCLING_PRODUCT_TYPES.TECH].includes(itemId)
          ? {
            ...prev,
            [RECYCLING_PRODUCT_TYPES.VIRTUAL]: count,
            [RECYCLING_PRODUCT_TYPES.TECH]: count,
          } : {
            ...prev,
            [itemId]: count,
          };

      setSubscriptionOrderItemsInStorage(newOrderItems);

      return newOrderItems;
    });
  }, []);

  const subtotal = useMemo(() => {
    const membershipItems = getFilteredMembershipItems(SUBSCRIPTION_PAYMENT_TYPES.ONE_TIME)
      .map(item => item.id);

    return getOrderSubtotal(orderItems, productPrices, membershipItems);
  }, [orderItems, productPrices]);

  const value = useMemo(() => ({
    isMobileSize,
    isLoading,
    isAnonymous,
    isFromCheckout,
    isSignInWithEmailLink,
    checkoutSessionId,
    paymentIntentId,
    registerCode,
    config,
    contentScrollRef,
    selectionScrollReentryCount,
    step,
    setStep,
    orderItems,
    onOrderItemsChange,
    productPrices,
    subscriptionPlan,
    optionSelected,
    setOptionSelected,
    subtotal,
    posthog,
    searchParams: query,
    elementsPaymentResult,
    setElementsPaymentResult,
  }), [
    config,
    isMobileSize,
    isLoading,
    step,
    isAnonymous,
    isFromCheckout,
    isSignInWithEmailLink,
    checkoutSessionId,
    paymentIntentId,
    registerCode,
    orderItems,
    onOrderItemsChange,
    productPrices,
    subscriptionPlan,
    optionSelected,
    setOptionSelected,
    subtotal,
    posthog,
    search,
    selectionScrollReentryCount,
    elementsPaymentResult,
  ]);

  useEffect(() => {
    const prev = previousSubscriptionStepRef.current;
    if (prev === null) {
      previousSubscriptionStepRef.current = step;
      return;
    }
    if (step === SUBSCRIPTION_STEPS.SELECTION && prev !== SUBSCRIPTION_STEPS.SELECTION) {
      setSelectionScrollReentryCount((c) => c + 1);
    }
    previousSubscriptionStepRef.current = step;
  }, [step]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (step !== SUBSCRIPTION_STEPS.PAYMENT || optionSelected != null) {
      return;
    }
    const sections = config?.steps?.[SUBSCRIPTION_STEPS.SELECTION]?.sections || [];
    const optionsSection = sections.find((s) => s.id === 'options');
    if (!optionsSection?.items?.length) {
      return;
    }
    const items = filterEnabledItems(optionsSection.items).filter(
      (item) => item.type !== SUBSCRIPTION_PAYMENT_TYPES.ONE_TIME,
    );
    const first = items[0];
    if (first?.uid) {
      setOptionSelected(first.uid);
    }
  }, [config, step, optionSelected]);

  // Redirect back to registration page when returning from a completed
  // subscription checkout during a pay-on-reg flow. Retries verification
  // in case the session is not yet finalized.
  useEffect(() => {
    if (!isFromCheckout || !registerCode || !checkoutSessionId) {
      return undefined;
    }

    const VERIFY_RETRY_DELAY = 1500;
    const VERIFY_MAX_RETRIES = 10;
    let cancelled = false;
    let retryTimeout = null;

    const redirectToRegister = () => {
      navigateWithSearch(ROUTE_REGISTER, { [REGISTER_CODE_PARAM]: registerCode, [REGISTER_FROM_CHECKOUT_PARAM]: 'true' }, true);
    };

    const verifyAndRedirect = async (attempt = 0) => {
      if (cancelled) return;

      const { data, error } = await getOrderCheckoutSession(checkoutSessionId);

      if (cancelled) return;

      if (!error && data?.status === STRIPE_CHECKOUT_SESSION_STATUS.COMPLETE) {
        redirectToRegister();
        return;
      }

      if (!error && data?.status === STRIPE_CHECKOUT_SESSION_STATUS.EXPIRED) {
        redirectToRegister();
        return;
      }

      if (attempt < VERIFY_MAX_RETRIES) {
        retryTimeout = setTimeout(() => verifyAndRedirect(attempt + 1), VERIFY_RETRY_DELAY);
        return;
      }

      // All retries exhausted — redirect anyway and let Register.jsx
      // handle verification with its own "Try again" fallback.
      redirectToRegister();
    };

    verifyAndRedirect();

    return () => {
      cancelled = true;
      if (retryTimeout) clearTimeout(retryTimeout);
    };
  }, [isFromCheckout, registerCode, checkoutSessionId, navigateWithSearch]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
